import React, { ChangeEvent, FC } from 'react';
import styled from 'styled-components';

import { SrOnly } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';

const UploadButton = styled(Text)`
  display: flex;
  align-items: center;
  display: flex;
  height: 38px;
  font-size: 12px;
  padding: 0 16px;
  gap: 8px;
  cursor: pointer;
  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.colors.lightGray};
  transition: all ease-out 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.black};
    border-color: rgba(34, 34, 34, 0.25);
  }
  &:active {
    color: ${({ theme }) => theme.colors.black};
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

interface Props {
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
}

const UploadImagesToResults: FC<Props> = ({ setImages }) => {
  const alerts = useAlerts();

  const validateFilesType = (files: FileList): boolean => {
    for (let i = 0; i < files.length; i++) {
      if (
        !['image/png', 'image/jpg', 'image/jpeg', 'image/webp'].includes(
          files[i].type,
        )
      ) {
        alerts.error(
          'Invalid file type!',
          'Please use an image of type png, jpg, or jpeg',
        );
        return false;
      }
    }
    return true;
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && validateFilesType(files)) {
      processFiles(files);
    }
  };

  const processFiles = (files: FileList) => {
    Array.from(files).forEach((file) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64String = reader.result as string;
        setImages((prev) => [base64String, ...(prev || [])]);
      };

      reader.readAsDataURL(file);
    });
  };

  return (
    <UploadButton as="label" $type="smallBold">
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5.19922 4.91797L7.99922 2.10547L10.7992 4.91797"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 9.60938V2.10938"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.3977 8.53711V13.3585C14.3977 13.5006 14.3415 13.6369 14.2414 13.7373C14.1414 13.8378 14.0058 13.8943 13.8643 13.8943H2.13099C1.98954 13.8943 1.85389 13.8378 1.75387 13.7373C1.65385 13.6369 1.59766 13.5006 1.59766 13.3585V8.53711"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Upload images
      <SrOnly
        as="input"
        type="file"
        id={`more-cover-images`}
        accept="image/*"
        multiple={true}
        onChange={onChange}
      />
    </UploadButton>
  );
};

export default UploadImagesToResults;
